// src/app/Router.tsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { appConfig } from '../config/app.config';
import { AppLayout } from '../components/layout/AppLayout';
import { PublicLayout } from '../components/layout/PublicLayout';
import { EmbedLayout } from '../components/layout/EmbedLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { LoginPage } from '../features/auth/LoginPage';
import { PatientLoginPage } from '../features/auth/PatientLoginPage';
import { HomePage } from '../features/home/HomePage';
import { DoctorsPage } from '../features/doctors/DoctorsPage';
import { BookingPage } from '../features/appointments/BookingPage';
import { AppointmentsPage } from '../features/appointments/AppointmentsPage';
import { ReceptionDashboard } from '../features/reception/ReceptionDashboard';
import { SettingsPage } from '../features/settings/SettingsPage';
import { DoctorManagementPage } from '../features/admin/DoctorManagementPage';
import { UserManagementPage } from '../features/admin/UserManagementPage';
import { QuickBookingPage } from '../features/reception/QuickBookingPage';
import { DoctorTodayPage } from '../features/doctor-dashboard/DoctorTodayPage';
import { PublicBookingPage } from '../features/public-booking/PublicBookingPage';
import { useAuthStore } from '../features/auth/auth.store';

function AuthRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />;
}

function PatientAuthRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : <PatientLoginPage />;
}

export function AppRouter() {
  return (
    <BrowserRouter basename={appConfig.deployment.basePath}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<AuthRedirect />} />
        <Route path="/patient-login" element={<PatientAuthRedirect />} />

        {/* Public booking — standalone page */}
        <Route element={<PublicLayout />}>
          <Route path="/public/book" element={<PublicBookingPage />} />
        </Route>

        {/* Public booking — iframe embed */}
        <Route element={<EmbedLayout />}>
          <Route path="/embed/book" element={<PublicBookingPage />} />
        </Route>

        {/* Protected — all authenticated users */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/doctors" element={<DoctorsPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/appointments/:number" element={<AppointmentsPage />} />

            {/* Reception / Admin */}
            <Route element={<ProtectedRoute allowedRoles={['Admin', 'Reception']} />}>
              <Route path="/reception/pending" element={<ReceptionDashboard />} />
              <Route path="/reception/queue" element={<ReceptionDashboard />} />
              <Route path="/reception/my-appointments" element={<ReceptionDashboard />} />
              <Route path="/reception/upcoming" element={<ReceptionDashboard />} />
              <Route path="/reception/quick-book" element={<QuickBookingPage />} />
            </Route>

            {/* Admin only — Settings & Management */}
            <Route element={<ProtectedRoute allowedRoles={['Admin']} />}>
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin/doctors" element={<DoctorManagementPage />} />
              <Route path="/admin/users" element={<UserManagementPage />} />
            </Route>

            {/* Doctor only */}
            <Route element={<ProtectedRoute allowedRoles={['Doctor', 'Admin']} />}>
              <Route path="/doctor/today" element={<DoctorTodayPage />} />
              <Route path="/doctor/my-patients" element={<DoctorTodayPage />} />
            </Route>
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
